/**
 * Article catalogue Excel/CSV import helpers (F7955).
 *
 * Spreadsheet contract:
 * - 1 data row = 1 variant.
 * - Create: rows sharing the same Name + Category (no article_id) → one article.
 * - Update: match via article_id / variant_id, then gtin, then sku.
 * - No stock quantities (use the Stocks sub-tab).
 */
import * as XLSX from '@e965/xlsx';
import i18n from '../locales/i18n';
import Utils from './Utils';
import { fitWorksheetColumns } from './excelExport';
import {
    IMPORT_HEADER_LANGS,
    annotateHeaderComments,
    buildHeaderAliases,
    normalizeHeaderLabel,
    sanitizeHeaderCell,
    stripHeaderHints,
} from './importSpreadsheetHeaders';

export const PRODUCT_ARTICLE_TYPE = 'product';
export const STOCK_TYPES = ['article', 'variant', 'none'];
export const STOCK_TYPE_VALUES_HINT = `(${STOCK_TYPES.join('|')})`;
export const FILE_EXTENSIONS = ['csv', 'xls', 'xlsx', 'ods', 'numbers'];

/** Internal fixed-column keys ↔ i18n suffix under `articles.import.columns.*`. */
export const ARTICLE_IMPORT_COLUMN_KEYS = Object.freeze([
    'article_id',
    'variant_id',
    'name',
    'short_description',
    'category',
    'stock_type',
    'supplier',
    'sort_weight',
    'pos',
    'variant_name',
    'gtin',
    'sku',
    'price',
    'purchasing_price',
    'vat',
    'stock_factor',
]);

const COLUMNS_I18N_PREFIX = 'articles.import.columns.';
const HELP_I18N_PREFIX = 'articles.import.column_help.';

/** English technical headers (backward compatible if someone pastes English). */
const LEGACY_HEADER_LABELS = {
    article_id: 'Article id',
    variant_id: 'Variant id',
    name: 'Name',
    short_description: 'Short description',
    category: 'Category',
    stock_type: 'Stock type',
    supplier: 'Supplier',
    sort_weight: 'Sort weight',
    pos: 'POS',
    variant_name: 'Variant name',
    gtin: 'GTIN',
    sku: 'SKU',
    price: 'Price',
    purchasing_price: 'Purchasing price',
    vat: 'VAT',
    stock_factor: 'Stock factor',
};

const sanitizeCell = sanitizeHeaderCell;

const normalizeColumnLang = lang => {
    const short = String(lang || i18n.language || 'en').split(/[-_]/)[0].toLowerCase();
    return IMPORT_HEADER_LANGS.includes(short) ? short : 'en';
};

/**
 * @param {string} [lang]
 * @returns {Record<string, string>}
 */
export const getArticleImportColumnLabels = lang => {
    const short = normalizeColumnLang(lang);
    const t = i18n.getFixedT(short, 'translations');
    const labels = {};
    ARTICLE_IMPORT_COLUMN_KEYS.forEach(key => {
        if (key === 'stock_type')
            labels[key] = t(`${COLUMNS_I18N_PREFIX}${key}`, { hint: STOCK_TYPE_VALUES_HINT });
        else
            labels[key] = t(`${COLUMNS_I18N_PREFIX}${key}`);
    });
    labels.sheet_name = t(`${COLUMNS_I18N_PREFIX}sheet_name`);
    return labels;
};

/**
 * @param {string} key
 * @param {string} [lang]
 * @returns {string}
 */
export const getArticleImportColumnHelp = (key, lang) => {
    const short = normalizeColumnLang(lang);
    const t = i18n.getFixedT(short, 'translations');
    const helpKey = `${HELP_I18N_PREFIX}${key}`;
    const text = t(helpKey, { hint: STOCK_TYPE_VALUES_HINT });
    return text === helpKey ? '' : text;
};

let headerAliasesCache = null;

const getHeaderAliases = () => {
    if (headerAliasesCache)
        return headerAliasesCache;

    headerAliasesCache = buildHeaderAliases({
        columnKeys: ARTICLE_IMPORT_COLUMN_KEYS,
        labelFor: (key, lang) => getArticleImportColumnLabels(lang)[key],
        langs: IMPORT_HEADER_LANGS,
        legacyLabels: LEGACY_HEADER_LABELS,
        extraAliases: [
            ['ean', 'gtin'],
            ['code ean', 'gtin'],
            ['ean code', 'gtin'],
            ['points de vente', 'pos'],
            ['salepoints', 'pos'],
            ['verkaufspunkte', 'pos'],
            ['punti vendita', 'pos'],
            // Static FR/DE/IT labels so parsing works even if i18n is not bootstrapped (tests).
            ['nom', 'name'],
            ['name', 'name'],
            ['catégorie', 'category'],
            ['kategorie', 'category'],
            ['categoria', 'category'],
            ['type de stock', 'stock_type'],
            ['lagertyp', 'stock_type'],
            ['tipo di stock', 'stock_type'],
            ['nom de variante', 'variant_name'],
            ['variantenname', 'variant_name'],
            ['nome variante', 'variant_name'],
            ['prix', 'price'],
            ['preis', 'price'],
            ['prezzo', 'price'],
            ['id article', 'article_id'],
            ['artikel-id', 'article_id'],
            ['id articolo', 'article_id'],
            ['id variante', 'variant_id'],
            ['varianten-id', 'variant_id'],
            ['id variante', 'variant_id'],
            ['description courte', 'short_description'],
            ['kurzbeschreibung', 'short_description'],
            ['descrizione breve', 'short_description'],
            ['fournisseur', 'supplier'],
            ['lieferant', 'supplier'],
            ['fornitore', 'supplier'],
            ['ordre d\'affichage', 'sort_weight'],
            ['anzeigereihenfolge', 'sort_weight'],
            ['ordine di visualizzazione', 'sort_weight'],
            ['prix d\'achat', 'purchasing_price'],
            ['einkaufspreis', 'purchasing_price'],
            ['prezzo di acquisto', 'purchasing_price'],
            ['tva', 'vat'],
            ['mwst.', 'vat'],
            ['iva', 'vat'],
            ['facteur de stock', 'stock_factor'],
            ['lagerfaktor', 'stock_factor'],
            ['fattore di stock', 'stock_factor'],
        ],
    });
    return headerAliasesCache;
};

/**
 * @param {string[]} headerRow
 * @returns {{ columns: Record<string, number>, unknown: string[], missingRequired: string[] }}
 */
export const mapHeaderRow = headerRow => {
    const aliases = getHeaderAliases();
    const columns = {};
    const unknown = [];

    (headerRow || []).forEach((label, index) => {
        const sanitized = sanitizeCell(label);
        if (!sanitized.length)
            return;
        const key = aliases[normalizeHeaderLabel(sanitized)]
            || aliases[normalizeHeaderLabel(stripHeaderHints(sanitized))];
        if (!key) {
            unknown.push(sanitized);
            return;
        }
        if (!(key in columns))
            columns[key] = index;
    });

    const missingRequired = [];
    // Required columns depend on create vs update — checked per row later.
    // At least one identity or name column must exist.
    if (!('name' in columns) && !('article_id' in columns) && !('gtin' in columns) && !('sku' in columns))
        missingRequired.push('name');

    return { columns, unknown, missingRequired };
};

const parseNumber = value => {
    const sanitized = sanitizeCell(value).replace(',', '.');
    if (!sanitized.length)
        return null;
    const parsed = Number(sanitized);
    return Number.isFinite(parsed) ? parsed : NaN;
};

const parsePosIds = value =>
    sanitizeCell(value)
        .split(/[,;|]/)
        .map(part => sanitizeCell(part))
        .filter(Boolean);

/**
 * @param {string[][]} rows
 * @param {Record<string, number>} columns
 * @returns {object[]}
 */
export const parseArticleRows = (rows, columns) => {
    const dataRows = (rows || []).slice(1);
    return dataRows.map((cells, index) => {
        const get = key => {
            if (!(key in columns))
                return '';
            return sanitizeCell(cells[columns[key]]);
        };

        const priceRaw = get('price');
        const purchasingRaw = get('purchasing_price');
        const vatRaw = get('vat');
        const stockFactorRaw = get('stock_factor');
        const sortWeightRaw = get('sort_weight');

        return {
            line: index + 2,
            articleId: get('article_id'),
            variantId: get('variant_id'),
            name: get('name'),
            shortDescription: get('short_description'),
            category: get('category'),
            stockType: get('stock_type').toLowerCase() || '',
            supplier: get('supplier'),
            sortWeight: sortWeightRaw.length ? parseNumber(sortWeightRaw) : null,
            posIds: parsePosIds(get('pos')),
            variantName: get('variant_name'),
            gtin: get('gtin'),
            sku: get('sku'),
            price: priceRaw.length ? parseNumber(priceRaw) : null,
            priceRaw,
            purchasingPrice: purchasingRaw.length ? parseNumber(purchasingRaw) : null,
            purchasingPriceRaw: purchasingRaw,
            vat: vatRaw.length ? parseNumber(vatRaw) : null,
            vatRaw,
            stockFactor: stockFactorRaw.length ? parseNumber(stockFactorRaw) : null,
            stockFactorRaw,
        };
    }).filter(row => (
        row.articleId
        || row.name
        || row.variantName
        || row.gtin
        || row.sku
        || row.category
    ));
};

/**
 * Index existing articles for match (id / gtin / sku).
 * @param {object[]} articles
 * @returns {{
 *   byId: Map<string, object>,
 *   byGtin: Map<string, { article: object, variant: object }>,
 *   bySku: Map<string, { article: object, variant: object }>,
 * }}
 */
export const buildArticleLookup = articles => {
    const byId = new Map();
    const byGtin = new Map();
    const bySku = new Map();

    (articles || []).forEach(article => {
        if (!article?._id)
            return;
        byId.set(article._id, article);
        (article.variants || []).forEach(variant => {
            if (!variant)
                return;
            const gtin = sanitizeCell(variant.gtin).toLowerCase();
            const sku = sanitizeCell(variant.sku).toLowerCase();
            if (gtin && !byGtin.has(gtin))
                byGtin.set(gtin, { article, variant });
            if (sku && !bySku.has(sku))
                bySku.set(sku, { article, variant });
        });
    });

    return { byId, byGtin, bySku };
};

const resolveMatch = (row, lookup) => {
    if (row.articleId) {
        const article = lookup.byId.get(row.articleId);
        if (!article)
            return { kind: 'error', reason: 'unknown_article' };
        let variant = null;
        if (row.variantId) {
            variant = (article.variants || []).find(v => v._id === row.variantId) || null;
            if (!variant)
                return { kind: 'error', reason: 'unknown_variant', article };
        } else if (row.gtin) {
            variant = (article.variants || []).find(
                v => sanitizeCell(v.gtin).toLowerCase() === row.gtin.toLowerCase()
            ) || null;
        } else if (row.sku) {
            variant = (article.variants || []).find(
                v => sanitizeCell(v.sku).toLowerCase() === row.sku.toLowerCase()
            ) || null;
        }
        return { kind: 'update', article, variant };
    }

    if (row.gtin) {
        const hit = lookup.byGtin.get(row.gtin.toLowerCase());
        if (hit)
            return { kind: 'update', article: hit.article, variant: hit.variant };
    }
    if (row.sku) {
        const hit = lookup.bySku.get(row.sku.toLowerCase());
        if (hit)
            return { kind: 'update', article: hit.article, variant: hit.variant };
    }

    return { kind: 'create' };
};

/**
 * Group rows into create/update operations.
 * @param {object[]} rows
 * @param {ReturnType<typeof buildArticleLookup>} lookup
 * @returns {Array<object>}
 */
export const groupArticleRows = (rows, lookup) => {
    const groups = [];
    const createBuckets = new Map();

    rows.forEach(row => {
        const match = resolveMatch(row, lookup);
        if (match.kind === 'error') {
            groups.push({
                action: 'error',
                reason: match.reason,
                rows: [row],
                article: match.article || null,
            });
            return;
        }

        if (match.kind === 'update') {
            const key = `update:${match.article._id}`;
            let group = groups.find(g => g.key === key);
            if (!group) {
                group = {
                    key,
                    action: 'update',
                    article: match.article,
                    rows: [],
                    variantMatches: [],
                };
                groups.push(group);
            }
            group.rows.push(row);
            group.variantMatches.push(match.variant);
            return;
        }

        const bucketKey = [
            sanitizeCell(row.name).toLowerCase(),
            sanitizeCell(row.category).toLowerCase(),
        ].join('\0');
        if (!createBuckets.has(bucketKey)) {
            const group = {
                key: `create:${bucketKey}`,
                action: 'create',
                name: row.name,
                rows: [],
            };
            createBuckets.set(bucketKey, group);
            groups.push(group);
        }
        createBuckets.get(bucketKey).rows.push(row);
    });

    return groups;
};

/**
 * @param {object} params
 * @returns {{ errors: object[], warnings: object[], valid: boolean, previewRows: object[] }}
 */
export const validateArticlesImport = ({
    headerMapping,
    groups,
    categoryIds,
}) => {
    const errors = [];
    const warnings = [];
    const previewRows = [];
    const categorySet = new Set(categoryIds || []);

    if (headerMapping?.missingRequired?.length) {
        headerMapping.missingRequired.forEach(column => {
            errors.push({ key: 'articles.import.missing_column', params: { column } });
        });
    }
    (headerMapping?.unknown || []).forEach(column => {
        warnings.push({ key: 'articles.import.unknown_column', params: { column } });
    });

    if (!groups.length)
        errors.push({ key: 'articles.import.no_rows' });

    groups.forEach(group => {
        if (group.action === 'error') {
            const row = group.rows[0];
            previewRows.push({
                action: 'error',
                reason: group.reason,
                line: row.line,
                name: row.name || row.articleId,
                variantName: row.variantName,
            });
            errors.push({
                key: `articles.import.${group.reason}`,
                params: { line: row.line, id: row.articleId || row.variantId },
            });
            return;
        }

        if (group.action === 'create') {
            const first = group.rows[0];
            const stockTypes = new Set(group.rows.map(r => r.stockType).filter(Boolean));
            const categories = new Set(group.rows.map(r => r.category).filter(Boolean));

            if (!first.name)
                errors.push({ key: 'articles.import.missing_name', params: { line: first.line } });
            if (!first.category)
                errors.push({ key: 'articles.import.missing_category', params: { line: first.line } });
            else if (categorySet.size && !categorySet.has(first.category)) {
                errors.push({
                    key: 'articles.import.unknown_category',
                    params: { category: first.category, line: first.line },
                });
            }
            if (stockTypes.size > 1) {
                errors.push({
                    key: 'articles.import.inconsistent_stock_type',
                    params: { name: group.name },
                });
            }
            if (categories.size > 1) {
                errors.push({
                    key: 'articles.import.inconsistent_category',
                    params: { name: group.name },
                });
            }

            const stockType = first.stockType || 'none';
            if (!STOCK_TYPES.includes(stockType)) {
                errors.push({
                    key: 'articles.import.invalid_stock_type',
                    params: { stockType: first.stockType || '∅', line: first.line },
                });
            }

            group.rows.forEach(row => {
                if (!row.variantName && group.rows.length > 1) {
                    errors.push({
                        key: 'articles.import.missing_variant_name',
                        params: { line: row.line },
                    });
                }
                if (row.price === null) {
                    errors.push({ key: 'articles.import.missing_price', params: { line: row.line } });
                } else if (Number.isNaN(row.price) || row.price < 0) {
                    errors.push({
                        key: 'articles.import.invalid_price',
                        params: { line: row.line, value: row.priceRaw },
                    });
                }
                ['purchasingPrice', 'vat', 'stockFactor', 'sortWeight'].forEach(field => {
                    if (row[field] !== null && Number.isNaN(row[field])) {
                        errors.push({
                            key: `articles.import.invalid_${field === 'purchasingPrice' ? 'purchasing_price' : field === 'stockFactor' ? 'stock_factor' : field === 'sortWeight' ? 'sort_weight' : field}`,
                            params: { line: row.line, value: row[`${field}Raw`] || row[field] },
                        });
                    }
                });
            });

            previewRows.push({
                action: 'create',
                name: first.name,
                category: first.category,
                stockType,
                variants: group.rows.length,
                lines: group.rows.map(r => r.line),
            });
            return;
        }

        // update
        group.rows.forEach((row, index) => {
            if (row.price !== null && (Number.isNaN(row.price) || row.price < 0)) {
                errors.push({
                    key: 'articles.import.invalid_price',
                    params: { line: row.line, value: row.priceRaw },
                });
            }
            if (row.stockType && !STOCK_TYPES.includes(row.stockType)) {
                errors.push({
                    key: 'articles.import.invalid_stock_type',
                    params: { stockType: row.stockType, line: row.line },
                });
            }
            if (row.category && categorySet.size && !categorySet.has(row.category)) {
                errors.push({
                    key: 'articles.import.unknown_category',
                    params: { category: row.category, line: row.line },
                });
            }

            previewRows.push({
                action: 'update',
                name: row.name
                    || Utils.localized_or_fallback(group.article?.name, i18n.language)
                    || group.article?._id,
                variantName: row.variantName
                    || Utils.localized_or_fallback(group.variantMatches[index]?.name, i18n.language)
                    || row.variantId
                    || '',
                articleId: group.article._id,
                variantId: group.variantMatches[index]?._id || row.variantId || '',
                line: row.line,
            });
        });
    });

    return {
        errors,
        warnings,
        valid: errors.length === 0,
        previewRows,
    };
};

const localizedName = (value, langs) => {
    const name = {};
    (langs || Utils.langs()).forEach(lang => {
        name[lang] = value || '';
    });
    return name;
};

const moneyBag = (amount, currency) => ({ [currency]: amount ?? 0 });

/**
 * Build create payloads for catalogue import groups.
 */
export const buildCreatePayloads = (groups, {
    currency = Utils.currency(),
    langs = Utils.langs(),
    vat = Utils.tax()?.vat_articles ?? 0,
    importedAt,
} = {}) => {
    return groups.filter(g => g.action === 'create').map(group => {
        const first = group.rows[0];
        const stockType = first.stockType || 'none';
        const defaultLang = Utils.default_lang() || langs[0] || 'fr';

        const variants = group.rows.map(row => {
            const variantLabel = row.variantName || row.name || '';
            const variantVat = row.vat === null || Number.isNaN(row.vat) ? vat : row.vat;
            const stockFactor = row.stockFactor === null || Number.isNaN(row.stockFactor)
                ? 1
                : row.stockFactor;
            return {
                name: localizedName(variantLabel, langs),
                stocks: [],
                stock_factor: stockFactor,
                gtin: row.gtin || '',
                sku: row.sku || '',
                vat: variantVat,
                price: moneyBag(row.price || 0, currency),
                value_like_price: true,
                value: moneyBag(row.price || 0, currency),
                purchasing_price: moneyBag(
                    row.purchasingPrice === null || Number.isNaN(row.purchasingPrice)
                        ? 0
                        : row.purchasingPrice,
                    currency
                ),
            };
        });

        const nameValue = first.name || '';
        const short = first.shortDescription || '';

        return {
            type: PRODUCT_ARTICLE_TYPE,
            name: localizedName(nameValue, langs),
            short_description: localizedName(short, langs),
            description: localizedName('', langs),
            category: { _id: first.category },
            pos: (first.posIds || []).map(id => ({ _id: id })),
            stock_type: stockType,
            stocks: [],
            supplier: first.supplier || '',
            sort_weight: first.sortWeight === null || Number.isNaN(first.sortWeight)
                ? 0
                : first.sortWeight,
            posters: [],
            tags: [],
            attributes: [],
            delivery: {},
            opaque: {
                imported_at: importedAt,
                import_lang: defaultLang,
            },
            variants,
        };
    });
};

/**
 * Merge update rows onto an existing article object (shallow clone for PUT).
 */
export const buildUpdatePayload = (group, {
    currency = Utils.currency(),
    langs = Utils.langs(),
    vat = Utils.tax()?.vat_articles ?? 0,
} = {}) => {
    const article = JSON.parse(JSON.stringify(group.article || {}));
    const first = group.rows[0];

    if (first.name)
        article.name = { ...(article.name || {}), [Utils.default_lang() || 'fr']: first.name };
    if (first.shortDescription) {
        article.short_description = {
            ...(article.short_description || {}),
            [Utils.default_lang() || 'fr']: first.shortDescription,
        };
    }
    if (first.category)
        article.category = { _id: first.category };
    if (first.stockType && STOCK_TYPES.includes(first.stockType))
        article.stock_type = first.stockType;
    if (first.supplier !== undefined && first.supplier !== '')
        article.supplier = first.supplier;
    if (first.sortWeight !== null && !Number.isNaN(first.sortWeight))
        article.sort_weight = first.sortWeight;
    if (first.posIds?.length)
        article.pos = first.posIds.map(id => ({ _id: id }));

    article.variants = article.variants || [];

    group.rows.forEach((row, index) => {
        let variant = group.variantMatches[index];
        if (variant) {
            variant = article.variants.find(v => v._id === variant._id) || variant;
        } else if (row.variantName || row.gtin || row.sku || row.price !== null) {
            // New variant on existing article
            variant = {
                name: localizedName(row.variantName || row.name || '', langs),
                stocks: [],
                stock_factor: 1,
                gtin: '',
                sku: '',
                vat,
                price: moneyBag(0, currency),
                value_like_price: true,
                value: moneyBag(0, currency),
                purchasing_price: moneyBag(0, currency),
            };
            article.variants.push(variant);
        } else {
            return;
        }

        const target = article.variants.find(v => v === variant || v._id === variant._id) || variant;
        if (row.variantName) {
            target.name = {
                ...(target.name || {}),
                [Utils.default_lang() || 'fr']: row.variantName,
            };
        }
        if (row.gtin !== '')
            target.gtin = row.gtin;
        if (row.sku !== '')
            target.sku = row.sku;
        if (row.price !== null && !Number.isNaN(row.price)) {
            target.price = moneyBag(row.price, currency);
            if (target.value_like_price !== false)
                target.value = moneyBag(row.price, currency);
        }
        if (row.purchasingPrice !== null && !Number.isNaN(row.purchasingPrice))
            target.purchasing_price = moneyBag(row.purchasingPrice, currency);
        if (row.vat !== null && !Number.isNaN(row.vat))
            target.vat = row.vat;
        if (row.stockFactor !== null && !Number.isNaN(row.stockFactor))
            target.stock_factor = row.stockFactor;
    });

    return article;
};

/**
 * Sample workbook with localized headers + help comments.
 */
export const buildSampleWorkbook = ({
    lang,
    categoryId = '',
    salepointIds = [],
} = {}) => {
    const labels = getArticleImportColumnLabels(lang);
    const headers = ARTICLE_IMPORT_COLUMN_KEYS.map(key => labels[key]);
    const sampleRow = [
        '',
        '',
        'Sample product',
        '',
        categoryId,
        'none',
        '',
        '0',
        (salepointIds || []).join(','),
        'Default',
        '',
        'SKU-001',
        '10',
        '',
        '',
        '1',
    ];

    const sheet = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    fitWorksheetColumns(sheet);
    annotateHeaderComments(
        sheet,
        ARTICLE_IMPORT_COLUMN_KEYS,
        key => getArticleImportColumnHelp(key, lang)
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, labels.sheet_name || 'Articles');
    return workbook;
};

export const readArticlesFile = async file => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName)
        throw new Error('articles.import.invalid_file_content');
    const sheet = workbook.Sheets[firstSheetName];
    return XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        defval: '',
        blankrows: false,
    });
};

export const prepareArticlesImport = ({
    rows,
    articles,
    categoryIds,
}) => {
    const headerMapping = mapHeaderRow(rows[0] || []);
    const parsedRows = parseArticleRows(rows, headerMapping.columns);
    const lookup = buildArticleLookup(articles);
    const groups = groupArticleRows(parsedRows, lookup);
    const validation = validateArticlesImport({
        headerMapping,
        groups,
        categoryIds,
    });

    return {
        headerMapping,
        parsedRows,
        groups,
        validation,
    };
};
